import { Alert, Button, Card, Field, Input } from '@platform/ui';
import { Link } from '@tanstack/react-router';
import { useState, type FormEvent } from 'react';

import { AuthLayout } from '../components/AuthLayout';
import { SecondFactorForm } from '../components/SecondFactorForm';
import { useMessages } from '../i18n';
import { ApiError } from '../lib/api';
import { usePageTitle } from '../lib/page-title';
import { problemText } from '../lib/problems';
import { useLogin } from '../lib/session';

export function SignInPage() {
    const t = useMessages();
    usePageTitle(t.auth.signInTitle);
    const login = useLogin();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');

    function onSubmit(event: FormEvent) {
        event.preventDefault();
        login.mutate({ email, password });
    }

    // The API answers every sign-in failure identically on purpose, so the panel
    // must not try to be more specific than the server was.
    const message =
        login.error instanceof ApiError
            ? login.error.isUnauthenticated
                ? t.auth.invalidCredentials
                : problemText(t, login.error)
            : login.error
              ? t.common.genericError
              : null;

    // Someone enrolled in two-step sign-in: the password opened no session,
    // and the code finishes what it started (docs/07 §4).
    const secondFactor = login.data?.secondFactor;
    if (secondFactor) {
        return (
            <AuthLayout>
                <SecondFactorForm
                    token={secondFactor.token}
                    onStartOver={() => {
                        login.reset();
                        setPassword('');
                    }}
                />
            </AuthLayout>
        );
    }

    return (
        <AuthLayout>
            <Card title={t.auth.signInTitle} description={t.auth.signInSubtitle}>
                <form onSubmit={onSubmit} noValidate className="flex flex-col gap-4">
                    {message && <Alert tone="danger">{message}</Alert>}

                    <Field label={t.auth.email} required>
                        {({ id, describedBy, invalid }) => (
                            <Input
                                id={id}
                                aria-describedby={describedBy}
                                invalid={invalid}
                                type="email"
                                name="email"
                                autoComplete="username"
                                autoFocus
                                required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                            />
                        )}
                    </Field>

                    <Field label={t.auth.password} required>
                        {({ id, describedBy, invalid }) => (
                            <Input
                                id={id}
                                aria-describedby={describedBy}
                                invalid={invalid}
                                type="password"
                                name="password"
                                autoComplete="current-password"
                                required
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                            />
                        )}
                    </Field>

                    <Button type="submit" block loading={login.isPending}>
                        {t.auth.signIn}
                    </Button>
                </form>
            </Card>

            <p className="mt-5 text-center text-13 text-muted-foreground">
                <Link to="/parola-unuttum" className="font-medium text-primary-strong hover:underline">
                    {t.auth.forgot.link}
                </Link>
                <br />
                {t.auth.noAccount}{' '}
                <Link to="/kayit" className="font-medium text-primary-strong hover:underline">
                    {t.auth.signUp}
                </Link>
            </p>
        </AuthLayout>
    );
}
